import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PageLoader } from '@/shared/ui/page-loader';

interface AuthRedirectProps {
  children: React.ReactNode;
}

const AuthRedirect: React.FC<AuthRedirectProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't redirect while loading
    if (loading) return;

    // Prefer centific SSO token if present, else fall back to app token
    const ssoAccess = localStorage.getItem('access_token');
    const token = ssoAccess || localStorage.getItem('auth_token');
    const isAuthenticated = !!(token && user);
    const isAuthPage = location.pathname === '/auth' || location.pathname.startsWith('/auth/');
    const isAcceptInvitationPage = location.pathname === '/accept-team-invitation';
    const isPublicBookingPage = location.pathname.startsWith('/book/');
    const isRootPage = location.pathname === '/';
    const searchParams = new URLSearchParams(location.search);
    const hasPendingSsoInUrl =
      searchParams.has('access_token') ||
      searchParams.has('refresh_token') ||
      searchParams.has('id_token') ||
      searchParams.has('token');
    // Microsoft's Entra redirect returns its response in the URL hash
    // (#code=... on success, #error=... on failure), not the query string -
    // without this check, this guard bounces to /auth before msalConfig's
    // handleRedirectPromise() ever gets a chance to read that hash, and a
    // plain navigate() to another path does not carry the old hash along,
    // silently destroying Microsoft's response.
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
    const hasPendingEntraInUrl = hashParams.has('code') || hashParams.has('error');

    // If user is authenticated and trying to access auth page, redirect to root
    if (isAuthenticated && isAuthPage) {
      navigate('/', { replace: true });
      return;
    }

    // If user is not authenticated and trying to access protected pages, redirect to auth.
    // Allow /accept-team-invitation flow and SSO token callback handoff to complete first.
    if (
      !isAuthenticated &&
      !isAuthPage &&
      !isAcceptInvitationPage &&
      !isPublicBookingPage &&
      !hasPendingSsoInUrl &&
      !hasPendingEntraInUrl
    ) {
      navigate('/auth', { replace: true });
      return;
    }
  }, [user, loading, navigate, location.pathname, location.search]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <PageLoader message="Loading..." size="xl" />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthRedirect;
